import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  CircleAlert,
  Coins,
  Gift,
  History,
  KeyRound,
  LoaderCircle,
  Sparkles,
  Ticket,
  Trophy,
  WalletCards,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  engagementApi,
  type CdkRecord,
  type CouponRecord,
  type ExchangeRecord,
  type RedPacketRecord,
} from "../api/engagement";
import { formatUsd } from "../utils/money";
import { useCopy } from "../i18n/copy";
import { useSession } from "../store/session";

function Header({
  title,
  back = "/rewards",
}: {
  title: string;
  description: string;
  back?: string;
}) {
  const { tr } = useCopy();
  return (
    <>
      <Link className="back-link" to={back}>
        <ArrowLeft />{" "}
        {tr(back === "/account" ? "Volver a mi cuenta" : "Volver a premios")}
      </Link>
      <header className="page-title-row engagement-title">
        <div>
          <p className="eyebrow">{tr("Beneficios GOP ALA")}</p>
          <h1>{tr(title)}</h1>
        </div>
      </header>
    </>
  );
}

export function RewardsHubPage() {
  const { user } = useSession();
  const { tr } = useCopy();
  const items = [
    {
      to: "/rewards/points",
      title: "Centro de puntos",
      copy: "Convierte puntos y consulta sus movimientos.",
      icon: Coins,
      tone: "green",
    },
    {
      to: "/rewards/raffle",
      title: "Centro de premios",
      copy: "Participa y revisa los resultados recientes.",
      icon: Trophy,
      tone: "gold",
    },
    {
      to: "/rewards/coupons",
      title: "Mis cupones",
      copy: "Consulta cupones disponibles y utilizados.",
      icon: Ticket,
      tone: "blue",
    },
    {
      to: "/rewards/cdk",
      title: "Canjear CDK",
      copy: "Ingresa un código y recibe su beneficio.",
      icon: KeyRound,
      tone: "earth",
    },
    {
      to: "/rewards/red-packets",
      title: "Código de regalo",
      copy: "Abre regalos y consulta el historial.",
      icon: Gift,
      tone: "red",
    },
    {
      to: "/rewards/exchange-history",
      title: "Historial de conversiones",
      copy: "Revisa conversiones anteriores entre puntos y saldo.",
      icon: History,
      tone: "green",
    },
  ];
  return (
    <main className="page rewards-page">
      <Header
        title="Puntos y premios"
        description="Administra tus beneficios desde un solo lugar."
        back="/account"
      />
      <section className="rewards-overview">
        <div>
          <small>{tr("Puntos disponibles")}</small>
          <strong>{user.score}</strong>
        </div>
        <div>
          <small>{tr("Saldo disponible")}</small>
          <strong>{formatUsd(user.balance)}</strong>
        </div>
        <Sparkles />
      </section>
      <section className="rewards-grid">
        {items.map(({ to, title, icon: Icon, tone }) => (
          <Link className={`reward-tile ${tone}`} to={to} key={to}>
            <span>
              <Icon />
            </span>
            <div>
              <h2>{tr(title)}</h2>
            </div>
            <ArrowRight />
          </Link>
        ))}
      </section>
    </main>
  );
}

export function CouponsPage() {
  const { tr } = useCopy();
  const [type, setType] = useState<1 | 2>(1);
  const query = useQuery({
    queryKey: ["coupons", type],
    queryFn: () => engagementApi.coupons(type),
  });
  return (
    <main className="page rewards-page">
      <Header
        title="Mis cupones"
        description="Consulta el valor y el estado de tus cupones."
      />
      <div className="segmented">
        <button
          className={type === 1 ? "active" : ""}
          onClick={() => setType(1)}
        >
          {tr("Disponibles")}
        </button>
        <button
          className={type === 2 ? "active" : ""}
          onClick={() => setType(2)}
        >
          {tr("Utilizados")}
        </button>
      </div>
      {query.isLoading ? (
        <div className="loading-row">
          <LoaderCircle className="spin" /> {tr("Cargando…")}
        </div>
      ) : (
        <section className="coupon-grid">
          {query.data?.map((coupon, index) => (
            <article
              className={type === 2 ? "used" : ""}
              key={coupon.id || index}
            >
              <div>
                <strong>{formatUsd(Number(coupon.coupon_quota))}</strong>
              </div>
              <span>
                <Ticket />
                <small>{tr("Cupón de descuento")}</small>
                <b>{tr(type === 1 ? "Disponible" : "Utilizado")}</b>
              </span>
            </article>
          ))}
          {!query.data?.length && (
            <div className="empty-state">
              <Ticket />
              <h2>{tr("No hay cupones")}</h2>
            </div>
          )}
        </section>
      )}
    </main>
  );
}

export function CdkPage() {
  const { user } = useSession();
  const { tr } = useCopy();
  const client = useQueryClient();
  const [code, setCode] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const history = useQuery({
    queryKey: ["cdk-records"],
    queryFn: engagementApi.cdkRecords,
    enabled: showHistory,
  });
  const redeem = useMutation({
    mutationFn: () => engagementApi.redeemCdk(code.trim()),
    onSuccess: () => {
      setCode("");
      client.invalidateQueries({ queryKey: ["cdk-records"] });
      client.invalidateQueries({ queryKey: ["session-user"] });
    },
  });
  return (
    <main className="page rewards-page">
      <Header
        title="Canjear CDK"
        description="Ingresa el código exactamente como aparece."
      />
      <section className="redeem-balance">
        <WalletCards />
        <span>
          <small>{tr("Saldo disponible")}</small>
          <strong>{formatUsd(user.balance)}</strong>
        </span>
        <button
          className="icon-action"
          aria-label={tr("Ver historial")}
          onClick={() => setShowHistory(!showHistory)}
        >
          <History />
        </button>
      </section>
      <section className="redeem-card">
        <span>
          <KeyRound />
        </span>
        <h2>{tr("Código CDK")}</h2>
        <label className="field-label" htmlFor="cdk-code">
          {tr("Código")}
        </label>
        <input
          id="cdk-code"
          value={code}
          onChange={(event) => setCode(event.target.value.toUpperCase())}
          autoComplete="off"
          placeholder="GOP ALA-XXXX"
        />
        {redeem.isSuccess && (
          <div className="form-feedback success">
            <BadgeCheck /> {tr("Código canjeado")}:{" "}
            {formatUsd(redeem.data.money)}
          </div>
        )}
        {redeem.isError && (
          <div className="form-feedback error">
            <CircleAlert /> {redeem.error.message}
          </div>
        )}
        <button
          className="button primary full"
          disabled={!code.trim() || redeem.isPending}
          onClick={() => redeem.mutate()}
        >
          {redeem.isPending ? <LoaderCircle className="spin" /> : <Gift />}{" "}
          {tr("Canjear código")}
        </button>
      </section>
      {showHistory && (
        <RecordSection
          title="Historial CDK"
          loading={history.isLoading}
          records={history.data?.list || []}
        />
      )}
    </main>
  );
}

export function RedPacketsPage() {
  const { tr } = useCopy();
  const client = useQueryClient();
  const [code, setCode] = useState("");
  const history = useQuery({
    queryKey: ["red-packet-records"],
    queryFn: engagementApi.redPacketRecords,
  });
  const open = useMutation({
    mutationFn: () => engagementApi.openRedPacket(code.trim()),
    onSuccess: () => {
      setCode("");
      client.invalidateQueries({ queryKey: ["red-packet-records"] });
      client.invalidateQueries({ queryKey: ["session-user"] });
    },
  });
  return (
    <main className="page rewards-page">
      <Header
        title="Código de regalo"
        description="Ingresa tu código para recibir el beneficio asociado."
      />
      <section className="gift-hero">
        <span>
          <Gift />
        </span>
        <div>
          <p className="eyebrow light">GOP ALA Ecuador</p>
          <h2>{tr("Abre tu regalo")}</h2>
        </div>
      </section>
      <section className="redeem-card red-redeem">
        <label className="field-label" htmlFor="gift-code">
          {tr("Código de regalo")}
        </label>
        <input
          id="gift-code"
          value={code}
          onChange={(event) => setCode(event.target.value.toUpperCase())}
          autoComplete="off"
          placeholder="ECU-XXXX"
        />
        {open.isSuccess && (
          <div className="gift-result">
            <BadgeCheck />
            <span>
              <small>{tr("Recibiste")}</small>
              <strong>{formatUsd(open.data.amount)}</strong>
            </span>
          </div>
        )}
        {open.isError && (
          <div className="form-feedback error">
            <CircleAlert /> {open.error.message}
          </div>
        )}
        <button
          className="button primary full"
          disabled={!code.trim() || open.isPending}
          onClick={() => open.mutate()}
        >
          {open.isPending ? <LoaderCircle className="spin" /> : <Gift />}{" "}
          {tr("Abrir regalo")}
        </button>
      </section>
      <RecordSection
        title="Historial de regalos"
        loading={history.isLoading}
        records={history.data || []}
      />
    </main>
  );
}

export function ExchangeHistoryPage() {
  const { tr } = useCopy();
  const query = useQuery({
    queryKey: ["exchange-records"],
    queryFn: engagementApi.exchangeRecords,
  });
  return (
    <main className="page rewards-page">
      <Header
        title="Historial de conversiones"
        description="Consulta las conversiones realizadas entre puntos y saldo."
      />
      {query.isLoading ? (
        <div className="loading-row">
          <LoaderCircle className="spin" /> {tr("Cargando historial…")}
        </div>
      ) : (
        <section className="conversion-list">
          {query.data?.map((record, index) => (
            <article key={`${record.time}-${index}`}>
              <span>
                <Coins />
              </span>
              <div>
                <strong>
                  {tr(
                    String(record.state) === "1"
                      ? "Puntos a saldo"
                      : "Saldo a puntos",
                  )}
                </strong>
                <small>{record.time}</small>
                <p>
                  {tr("Consumido")}: {record.consume}
                </p>
              </div>
              <b>{record.exchange}</b>
            </article>
          ))}
        </section>
      )}
    </main>
  );
}

function RecordSection({
  title,
  loading,
  records,
}: {
  title: string;
  loading: boolean;
  records: Array<CdkRecord | RedPacketRecord>;
}) {
  const { tr } = useCopy();
  return (
    <section className="reward-history">
      <div className="section-heading">
        <div>
          <p className="eyebrow">{tr("Actividad")}</p>
          <h2>{tr(title)}</h2>
        </div>
      </div>
      {loading ? (
        <div className="loading-row">
          <LoaderCircle className="spin" /> {tr("Cargando historial…")}
        </div>
      ) : (
        records.map((record, index) => (
          <article key={`${record.code}-${index}`}>
            <span>
              <Gift />
            </span>
            <div>
              <strong>{record.code}</strong>
              <small>{record.use_time}</small>
            </div>
            <b>{formatUsd(Number(record.money))}</b>
          </article>
        ))
      )}
      {!loading && !records.length && (
        <div className="empty-state">
          <History />
          <h2>{tr("Sin registros")}</h2>
        </div>
      )}
    </section>
  );
}
